import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { dbService } from "../../firebaseInstance";
import { useUserDispatch, UserTypes } from "../../contexts/UserContext";
import { arrayUnion } from "firebase/firestore";
import { errorLogger } from '../../utils/errorLogger';
const Callback = () => {
	const navigate = useNavigate();
	const location = useLocation();
	// 토큰 교환은 백엔드(exchangeToken)에서 server-to-server 로 수행한다.
	// client_secret 은 절대 프론트엔드에 두지 않는다 (CORS + 보안).
	const exchangeTokenUrl = "https://us-central1-my-threads-insight.cloudfunctions.net/exchangeToken";
	// 인가 시작 시점에 저장한 redirect_uri (테스트 모드에서만 사용)
	const storedRedirectUri = (() => {
		try { return sessionStorage.getItem('oauth_redirect_uri'); } catch (_) { return null; }
	})();
	const dispatch = useUserDispatch();

	useEffect(() => {
		errorLogger.startTrace({ flow: 'login_callback' });
		errorLogger.trace('auth_start');
		
		// 테스트 모드: 콜백에 머무르지 않고 로그인 페이지로 자동 복귀하여 콘솔 테스트
		try {
			const params = new URLSearchParams(location.search);
			const codeParam = params.get('code');
			const stateParam = params.get('state');
			const isTestMode = sessionStorage.getItem('login_test_mode') === '1' || stateParam === 'test';
			if (isTestMode && codeParam && stateParam) {
				const target = `/?code=${encodeURIComponent(codeParam)}&state=${encodeURIComponent(stateParam)}`;
				navigate(target, { replace: true });
				return; // 콜백에서 교환 로직 수행하지 않음
			}
		} catch (_) {}
		
		// 디버깅: 여러 방식으로 code 파싱 비교
		const locationCode = new URLSearchParams(location.search).get("code");
		const directCode = new URL(window.location.href).searchParams.get("code");
		const regexCode = window.location.search.match(/[?&]code=([^&]+)/)?.[1];
		const startedOrigin = (() => { try { return sessionStorage.getItem('oauth_start_origin'); } catch(_) { return null; } })();
		
		console.log('=== Code 파싱 비교 & redirect_uri 체크 ===');
		console.log('1. location.search:', location.search);
		console.log('2. window.location.href:', window.location.href);
		console.log('3. locationCode:', locationCode);
		console.log('4. directCode:', directCode);
		console.log('5. regexCode:', regexCode);
		console.log('6. stored redirect_uri:', storedRedirectUri);
		console.log('7. started origin:', startedOrigin);
		console.log('8. current origin:', window.location.origin);
		
		// 직접 URL에서 파싱한 code 사용
		const authorizationCode = directCode || regexCode;
		
		if (authorizationCode) {
			// 교환 락: 동일 코드 재교환 방지
			try {
				const used = sessionStorage.getItem('oauth_code_used');
				if (used && used === authorizationCode) {
					console.warn('⚠️ Skipping exchange: code already used');
					return;
				}
				// 교환 시작 전에 먼저 락 설정
				sessionStorage.setItem('oauth_code_used', authorizationCode);
			} catch (_) {}
			console.log('🔑 Authorization code received:', authorizationCode.slice(0, 20) + '...');
			// 정상 플로우는 /callback 고정, 테스트 모드만 storedRedirectUri 사용
			const isTestMode = (sessionStorage.getItem('login_test_mode') === '1' || new URLSearchParams(location.search).get('state') === 'test');
			const effectiveRedirectUri = (isTestMode && storedRedirectUri) ? storedRedirectUri : `${window.location.origin}/callback`;
			console.log('🔗 Redirect URI:', effectiveRedirectUri);
			errorLogger.trace('code_received', { hasCode: true });
			
			// URL에서 code 파라미터 즉시 제거하여 재사용 방지
			const newUrl = window.location.pathname;
			window.history.replaceState({}, document.title, newUrl);
			
			// Clear existing tokens before exchanging the new code
			localStorage.removeItem("accessToken");
			localStorage.removeItem("userID");
			exchangeCodeForToken(authorizationCode, effectiveRedirectUri);
		}
	}, []); // location 의존성 제거하여 한 번만 실행

    const exchangeCodeForToken = async (authorizationCode, redirectUriParam) => {
        try {
            // 백엔드에서 code → 단기 토큰 → 장기 토큰 → 프로필까지 한 번에 교환
            // (graph.threads.net 직접 호출은 CORS 차단 + client_secret 노출 문제로 금지)
            const response = await axios.post(
                exchangeTokenUrl,
                { code: authorizationCode, redirect_uri: redirectUriParam },
                { headers: { 'Content-Type': 'application/json' } }
            );

            console.log("response from exchange code for token", response);
            errorLogger.trace('token_exchanged', { hasUserId: !!response?.data?.user_id });

            const accessToken = response.data.access_token;
            const longLivedToken = response.data.long_lived_token;
            console.log("longLivedToken : ", longLivedToken);

            // 프로필도 백엔드에서 함께 반환
            const profileData = response.data.profile;

            // 🔧 기존 방식 유지: 모든 곳에서 profile.id 사용
            console.log("id from exchange code for token", response.data.user_id);
            console.log("id from profile response", profileData.id);

            errorLogger.trace('profile_loaded', { userId: profileData.id });
            localStorage.setItem("userID", profileData.id);

            const id = profileData.id;

            // Save tokens using tokenManager
            const { saveTokens } = await import('../../utils/tokenManager');
            await saveTokens(id, accessToken, longLivedToken);
            errorLogger.trace('tokens_saved', { userId: id });
            console.log("✅ Tokens saved via tokenManager");

            // Store in Firestore
            const userDocRef = dbService.collection("user").doc(id);

            console.log("get user id from access token", id);

                try {
                    const userDoc = await userDocRef.get();
                    console.log("check if user doc exists", userDoc);
                    const existing = userDoc.exists ? userDoc.data() : null;
                    const preservedSignUp = existing?.profile?.signUpDate || new Date().toISOString();

                    const userData = {
                        access_token: response.data.access_token,
                        user_id: profileData.id,
                        visitLog: arrayUnion(new Date().toISOString()),
                        profile: {
                            ...profileData,
                            signUpDate: userDoc.exists ? preservedSignUp : preservedSignUp,
                        },
                    };

                    if (!userDoc.exists) {
                        console.log("create new user doc", userData);
                        await userDocRef.set(userData).catch((error) => {
                            console.error("Error creating user document:", error);
                        });
                        console.log("Sign up");
                        errorLogger.trace('firestore_user_created', { userId: id });
                    } else {
                        console.log("update existing user doc", userData);
                        await userDocRef.update(userData).catch((error) => {
                            console.error("Error updating user document:", error);
                        });
                        console.log("Sign in");
                        errorLogger.trace('firestore_user_updated', { userId: id });
                    }

                    // Context 저장
                    console.log("setting profile in context with profileData", profileData);
                    dispatch({
                        type: UserTypes.SET_PROFILE,
                        payload: {
                            profile: profileData,
                            accessToken: longLivedToken,
                        },
                    });
                } catch (firestoreError) {
                    console.error("Firestore operation failed:", firestoreError);
                    await errorLogger.flushOnError({
                        component: 'Callback',
                        event: 'firestore_user_write_failed',
                        status: firestoreError?.response?.status,
                        endpoint: 'firestore:user',
                        stack: firestoreError?.stack
                    });
                    // 오류여도 최소한 컨텍스트는 설정해 이동 가능하게
                    dispatch({
                        type: UserTypes.SET_PROFILE,
                        payload: {
                            profile: profileData,
                            accessToken: longLivedToken,
                        },
                    });
                }
                console.log("✅ User context updated, App.js will handle navigation");

                // 매거진 페이지에서 왔는지 확인
                const returnUrl = sessionStorage.getItem("magazine_return_url");
                if (returnUrl) {
                    console.log("Redirecting to magazine page:", returnUrl);
                    window.location.href = returnUrl;
                    return; // 여기서 리턴하여 추가 리다이렉트 방지
                }

                // 커스텀 리다이렉트 경로 확인
                const redirectTo = sessionStorage.getItem('login_redirect_to');
                if (redirectTo) {
                    sessionStorage.removeItem('login_redirect_to');
                    console.log("🔄 Redirecting to custom path:", redirectTo);
                    navigate(redirectTo, { replace: true });
                    return; // App.js 자동 리다이렉트 방지
                }

                // App.js의 useEffect가 isLogin 상태 변경을 감지하여 자동으로 /dashboard로 리다이렉트
        } catch (error) {
            console.error("Error during authentication:", error);
            
            // 에러 발생 시에도 URL에서 code 파라미터 제거
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            // 에러 페이지 라우트가 없으므로 랜딩으로 복귀
            navigate("/", { replace: true });
        }
    };

	return <div>Processing...</div>;
};

export default Callback;
